/**
 * 🎯 Event Manager - Gestión de eventos
 * @version 1.0.0
 * @description Gestor de eventos para el chat widget
 */

export class EventManager {
  constructor() {
    this.listeners = new Map();
    this.config = {
      debug: false,
    };
  }

  /**
   * 🎯 Configura los event listeners del chat
   * @param {Object} elements - Elementos del DOM
   * @param {Object} callbacks - Callbacks para los eventos
   */
  setupEventListeners(elements, callbacks) {
    const { chatButton, chatCloseButton, chatForm, chatOptionsElement } =
      elements;
    const { toggleChat, handleMessageSubmit, handleOptionClick } = callbacks;

    // Toggle chat
    this._addListener(chatButton, "click", toggleChat);
    this._addListener(chatCloseButton, "click", toggleChat);

    // Enviar mensaje
    this._addListener(chatForm, "submit", handleMessageSubmit);

    // Opciones rápidas
    this._addListener(chatOptionsElement, "click", handleOptionClick);

    if (this.config.debug) {
      console.log("🎯 [Event Manager] Event listeners configurados");
    }
  }

  /**
   * 🔗 Añade un event listener y lo registra
   * @private
   * @param {HTMLElement} element - Elemento del DOM
   * @param {string} event - Tipo de evento
   * @param {Function} callback - Función callback
   */
  _addListener(element, event, callback) {
    if (!element) return;

    element.addEventListener(event, callback);

    // Guardar referencia para poder remover después
    if (!this.listeners.has(element)) {
      this.listeners.set(element, []);
    }
    this.listeners.get(element).push({ event, callback });
  }

  /**
   * 🗑️ Remueve todos los event listeners registrados
   */
  removeAllListeners() {
    this.listeners.forEach((eventList, element) => {
      eventList.forEach(({ event, callback }) => {
        if (element && typeof element.removeEventListener === "function") {
          element.removeEventListener(event, callback);
        }
      });
    });

    this.listeners.clear();

    if (this.config.debug) {
      console.log("🗑️ [Event Manager] Todos los event listeners removidos");
    }
  }

  /**
   * 🔄 Remueve event listeners de un elemento específico
   * @param {HTMLElement} element - Elemento del DOM
   */
  removeElementListeners(element) {
    if (this.listeners.has(element)) {
      const eventList = this.listeners.get(element);
      eventList.forEach(({ event, callback }) => {
        element.removeEventListener(event, callback);
      });
      this.listeners.delete(element);
    }
  }

  /**
   * ⚙️ Actualiza la configuración del gestor de eventos
   * @param {Object} newConfig - Nueva configuración
   */
  updateConfig(newConfig) {
    this.config = { ...this.config, ...newConfig };
  }

  /**
   * 📊 Obtiene información sobre los listeners registrados
   * @returns {Object} Información de los listeners
   */
  getListenersInfo() {
    const info = {
      totalElements: this.listeners.size,
      totalListeners: 0,
      elementDetails: [],
    };

    this.listeners.forEach((eventList, element) => {
      info.totalListeners += eventList.length;
      info.elementDetails.push({
        element: element.tagName || "Unknown",
        className: element.className || "",
        events: eventList.map(({ event }) => event),
      });
    });

    return info;
  }
}
